import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Package, Clock, Truck, Check, ArrowLeft, MapPin, Receipt
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const STATUS_CONFIG = {
    pending: { label: 'Pending', color: 'text-muted-foreground', bg: 'bg-muted/50', icon: Clock },
    confirmed: { label: 'Confirmed', color: 'text-electric-blue', bg: 'bg-electric-blue/10', icon: Check },
    shipped: { label: 'Shipped', color: 'text-cyber-purple', bg: 'bg-cyber-purple/10', icon: Package },
    in_transit: { label: 'In Transit', color: 'text-boltz-gold', bg: 'bg-boltz-gold/10', icon: Truck },
    delivered: { label: 'Delivered', color: 'text-neon-green', bg: 'bg-neon-green/10', icon: Check },
    cancelled: { label: 'Cancelled', color: 'text-plasma-red', bg: 'bg-plasma-red/10', icon: Clock },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
        <Badge className={`${cfg.bg} ${cfg.color} border-0 text-[10px]`} data-testid={`status-${status}`}>
            <Icon size={10} className="mr-0.5" weight="bold" />{cfg.label}
        </Badge>
    );
}

function StatusTimeline({ history }) {
    if (!history?.length) return null;
    return (
        <div className="space-y-2 mt-3 pl-3 border-l-2 border-border">
            {history.map((h, i) => {
                const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.pending;
                return (
                    <div key={i} className="relative pl-4">
                        <div className={`absolute -left-[9px] w-3 h-3 rounded-full border-2 border-background ${cfg.bg}`} />
                        <p className={`text-xs font-bold ${cfg.color}`}>{cfg.label}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(h.at).toLocaleString()}</p>
                    </div>
                );
            })}
        </div>
    );
}

export default function OrderHistory() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        fetch(`${API}/orders/history`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : { orders: [], total: 0 })
            .then(d => { setOrders(d.orders); setTotal(d.total); })
            .finally(() => setLoading(false));
    }, []);

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <Button size="sm" variant="ghost" onClick={() => navigate('/shop')} className="mb-2 text-muted-foreground">
                        <ArrowLeft size={16} className="mr-1" />Shop
                    </Button>
                    <span className="label-text text-primary text-glow">PURCHASES</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="orders-title">
                        Order History
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{total} total orders</p>
                </motion.div>

                {loading ? (
                    <div className="space-y-3">
                        {[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
                    </div>
                ) : orders.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-8 text-center">
                            <Receipt size={48} className="text-muted-foreground mx-auto mb-4" />
                            <p className="font-medium text-foreground">No orders yet</p>
                            <p className="text-sm text-muted-foreground mt-1">Your purchase history will appear here</p>
                            <Button onClick={() => navigate('/shop')} className="mt-4 btn-primary" data-testid="go-shop-btn">
                                Browse Shop
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {orders.map(order => (
                            <motion.div key={order.order_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                                <Card className="glass-card" data-testid={`order-${order.order_id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="font-bold text-sm text-foreground">{order.item_name || 'Purchase'}</p>
                                                <p className="text-[10px] font-mono text-muted-foreground">{order.order_id}</p>
                                            </div>
                                            <StatusBadge status={order.delivery_status || 'pending'} />
                                        </div>

                                        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                                            <span className="font-mono text-neon-gold">{order.amount?.toLocaleString()} Boltz</span>
                                            <span>{new Date(order.created_at).toLocaleDateString()}</span>
                                            {order.tracking_number && (
                                                <span className="flex items-center gap-1">
                                                    <MapPin size={10} />{order.tracking_number}
                                                </span>
                                            )}
                                        </div>

                                        <StatusTimeline history={order.status_history} />
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
